import { ReactQueryStaleTime } from '@openx/constants/react-query';
import type { Account } from '@openx/types/account';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../components/AuthContext/context';

export const AUDIENCE_SHARE_RECIPIENTS_PATH = '/api/mgmt/account?type_full=account.partner&limit=0';

export type AudienceShareRecipientsResponse = {
  limit: number;
  offset: number;
  has_more: boolean;
  objects: Account[];
};

type UseAudienceShareRecipientsResult = {
  recipients: Account[];
  error: Error | null;
  loading: boolean;
};

const EMPTY_RECIPIENTS: Account[] = [];

export const useAudienceShareRecipients = (): UseAudienceShareRecipientsResult => {
  const { currentUser } = useAuth();

  const { data, error, isLoading } = useQuery({
    queryFn: async (): Promise<AudienceShareRecipientsResponse> => {
      const token = await currentUser?.getIdToken();
      const response = await fetch(AUDIENCE_SHARE_RECIPIENTS_PATH, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`Audience share recipients request returned ${response.status}`);
      }

      return response.json();
    },
    queryKey: [QueryKeys.AUDIENCE_SHARE_RECIPIENTS],
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  return {
    recipients: data?.objects ?? EMPTY_RECIPIENTS,
    error,
    loading: isLoading,
  };
};
